import React, { useEffect, useState } from "react";
import { Button, Col, Input, Row, Select, Table } from "antd";
import { toast } from "react-hot-toast";
import {
  collection,
  getDocs,
  doc,
  deleteDoc,
  query,
  where,
} from "firebase/firestore";
import axios from "axios";
import { auth, db } from "../../../firebase";
import PageHeading from "../../../components/common/PageHeading";

import "./ManageUser.css";
import { useNavigate } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";

const roles = [
  {
    value: "President",
  },
  {
    value: "Accountant",
  },
  {
    value: "Property Manager",
  },
  {
    value: "On-Site Manager",
  },
  {
    value: "Leasing Agent",
  },
  {
    value: "Accounts Payable",
  },
  {
    value: "Accounts Receivable",
  },
  {
    value: "View Only",
  },
  {
    value: "Maintenance Coordinator",
  },
  {
    value: "Maintenance Tech",
  },
  {
    value: "Regional Manager",
  },
  {
    value: "Assistant Property Manager",
  },
  {
    value: "Assistant Leasing Agent",
  },
];

function ManageUsers() {
  const [data, setData] = useState([]);
  const [filterName, setFilterName] = useState("");
  const [filterRole, setFilterRole] = useState("");
  const [filterPropertyGroup, setFilterPropertyGroup] = useState("");
  const [filterMore, setFilterMore] = useState(false);
  const [user, loading] = useAuthState(auth);
  const [companyName, setCompanyName] = useState("");
  const [isCompanyNameFetched, setIsCompanyNameFetched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const fetchCompanyName = async () => {
    try {
      const usersRef = collection(db, "allUsers");
      const userData = query(usersRef, where("uid", "==", user?.uid));

      const querySnapshot = await getDocs(userData);
      querySnapshot.forEach((doc) => {
        const res = doc.data();
        setCompanyName(res.companyName);
      });

      setIsCompanyNameFetched(true);
    } catch (error) {
      console.log("Error getting company name: ", error);
    }
  };

  useEffect(() => {
    fetchCompanyName();
  }, [user, loading]);

  useEffect(() => {
    if (!isCompanyNameFetched) return;
    setIsLoading(true);

    const fetchUsers = async () => {
      try {
        const userRef = collection(db, "companies", companyName, "users");
        const snapshot = await getDocs(userRef);
        const fetchedData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        setData(fetchedData);
      } catch (error) {
        console.error(error);
        alert(error.message);
      } finally {
        setIsLoading(false); // stop loading
      }
    };

    fetchUsers();
  }, [companyName, isCompanyNameFetched]);

  const handleDeleteUser = async (docId, userId) => {
    try {
      const userRef = doc(db, "companies", companyName, "users", docId);
      await deleteDoc(userRef);
      toast.success("User Deleted Successfully");
    } catch (err) {
      console.error(err);
      alert(err.message);
    }
    try {
      const q = query(collection(db, "allUsers"), where("uid", "==", userId));

      // Get the matching user documents
      const querySnapshot = await getDocs(q);

      // Iterate over the querySnapshot and delete each document
      querySnapshot.forEach(async (doc) => {
        await deleteDoc(doc.ref);
      });
      toast.success("User Deleted Successfully");
    } catch (err) {
      console.error(err);
      alert(err.message);
    }

    try {
      await axios.delete(
        `https://us-central1-sayeagle-77c75.cloudfunctions.net/api/delete-user/${userId}`
      );
      console.log("User deleted successfully.");
    } catch (error) {
      console.error("Error deleting the user:", error);
    }
  };

  const filteredDataSource = data.filter(
    (record) =>
      (filterName === "" ||
        record?.profile?.firstName
          ?.toLowerCase()
          .includes(filterName.toLowerCase())) &&
      (filterRole === "" ||
        record?.profile?.role
          ?.toLowerCase()
          .includes(filterRole.toLocaleLowerCase())) &&
      (filterPropertyGroup === "" ||
        record.propertyPermission?.includes(filterPropertyGroup))
  );

  const clearFilters = () => {
    setFilterName("");
    setFilterRole("");
    setFilterPropertyGroup("");
  };

  const handleReinvite = async (email, uid, role, companyName) => {
    const userData = {
      uid: uid,
      email: email,
      role: role,
      companyName: companyName,
    };

    try {
      const response = await axios.post(
        "https://us-central1-sayeagle-77c75.cloudfunctions.net/api/reinvite_verification",
        userData
      );
      console.log(response.data); // User created successfully
      toast.success("email sent successfully!");
    } catch (error) {
      console.error("Error creating user:", error);
      const errorMessage = error.message;
      toast.error(errorMessage);
    }
  };

  const navigate = useNavigate();

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (_, record) => (
        <span
          className="onclick-text"
          onClick={() => navigate(`/mysettings/${record?.id}`)}
        >{`${record?.profile?.firstName} ${record?.profile?.lastName}`}</span>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (_, record) => <span>{record?.profile?.email}</span>,
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      render: (_, record) => <span>{record?.profile?.role}</span>,
    },
    // {
    //   title: "Last Login",
    //   dataIndex: "lastLogin",
    //   key: "lastLogin",
    //   render: (_, record) => (
    //     <span>
    //       {record?.activited ? "25/08/2013" : "Account Not Activated"}
    //     </span>
    //   ),
    // },
    {
      title: "2FA Required",
      dataIndex: "faRequired",
      key: "faRequired",
      render: () => <span>Yes</span>,
    },
    {
      title: "2FA Activated",
      dataIndex: "faActivate",
      key: "faActivate",
      render: () => <span>Yes</span>,
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      render: (_, record) => (
        <Row align="middle" gutter={[8, 0]}>
          {!record?.activited ? (
            <Col>
              <span
                className="onclick-text"
                onClick={() =>
                  handleReinvite(
                    record?.profile?.email,
                    record?.uid,
                    record?.profile?.role,
                    record?.companyDetails.companyName
                  )
                }
              >
                Reinvite
              </span>
            </Col>
          ) : null}
          <Col>
            <span
              className="onclick-red-text"
              onClick={() => handleDeleteUser(record?.id, record?.uid)}
            >
              Delete
            </span>
          </Col>
        </Row>
      ),
    },
  ];

  return (
    <div className="manage-users-container page-container">
      <div className="manage-users main-container">
        <PageHeading heading="Manage Users" />
        <div className="users">
          <Row>
            <Col className="user-form" span={24}>
              <div className="input-container">
                <label htmlFor="name">Name</label>
                <Input
                  placeholder="View by name"
                  value={filterName}
                  onChange={(e) => setFilterName(e.target.value)}
                />
              </div>
              <div className="input-container">
                <label htmlFor="name">Role</label>
                <Select
                  options={roles}
                  value={filterRole}
                  onChange={(value) => setFilterRole(value)}
                  placeholder="View by property group or portfolio"
                />
              </div>
              {/* {filterMore ? (
                <div className="input-container">
                  <label htmlFor="name">
                    Can view Property Group or Portfolio
                  </label>
                  <Select
                    options={[{ value: "Dewitt" }, { value: "Test 2" }]}
                    value={filterPropertyGroup}
                    onChange={(value) => setFilterPropertyGroup(value)}
                    placeholder="View by property group or portfolio"
                  />
                </div>
              ) : null} */}
            </Col>
          </Row>
          {/* <Row>
            <Col className="filter-actions-container" span={24}>
              <Button onClick={() => setFilterMore(!filterMore)}>
                {filterMore ? "Less Filters..." : "More Filters..."}
              </Button>
              <Button onClick={() => clearFilters()}>Clear Filters</Button>
            </Col>
          </Row> */}
          <Row>
            <Col className="users-list" span={24}>
              <Table
                columns={columns}
                dataSource={filteredDataSource}
                loading={isLoading}
                rowKey={(obj) => obj.id}
              />
            </Col>
          </Row>
        </div>
      </div>
      <div className="RightSidebar"></div>
    </div>
  );
}

export default ManageUsers;
